import { useSavedChartsStore } from '@/stores/savedChartsStore';
import { useAuthentication } from '@/composables/useAuthentication';
import { fetchChartLists } from '@/composables/fetchChartLists';
import { parseChartLists } from '@/composables/parseChartLists';
import { sortObjectsClosure } from '@/utils/sortObjectsClosure';
import { SavedChartsStore } from '@/types/SavedChartsStore';

let refreshChartListsPromise: Promise<void> | null = null;

async function _refreshChartLists() {
  const savedChartsStore = useSavedChartsStore();

  try {
    const rawChartLists = await fetchChartLists();
    let chartLists = parseChartLists(rawChartLists);

    const readonlyChartLists = chartLists.filter(chartList => chartList.isReadonly);
    const allOtherChartLists = chartLists.filter(chartList => !chartList.isReadonly);

    readonlyChartLists.sort(sortObjectsClosure({
      propertyName: 'htmlSanitizedName',
      order: 'ascending'
    }));
    allOtherChartLists.sort(sortObjectsClosure({
      propertyName: 'htmlSanitizedName',
      order: 'ascending'
    }));

    chartLists = [
      ...readonlyChartLists,
      ...allOtherChartLists
    ];

    savedChartsStore.$patch((state) => {
      state.isAttemptingToLoadChartLists = false;
      state.hasAttemptedToLoadChartLists = true;
      state.hasFailedToLoadChartLists = false;

      for (const chartList of state.chartLists) {
        const updatedChartList = chartLists.find(cl => cl.recid === chartList.recid);

        if (updatedChartList) {
          updatedChartList.charts = chartList.charts;
        }
      }

      state.chartLists = chartLists;
    });
  } catch (e) {
    window.sc3WorkbenchLogger.error('Failed to refresh ChartLists.', e);

    const patchObject: Partial<SavedChartsStore> = {
      isAttemptingToLoadChartLists: false,
      hasAttemptedToLoadChartLists: true
    };

    if (!savedChartsStore.hasAttemptedToLoadChartLists) {
      patchObject.hasFailedToLoadChartLists = true;
    }

    savedChartsStore.$patch(patchObject);
  }
}

export async function refreshChartLists() {
  const savedChartsStore = useSavedChartsStore();
  const { isLoggedIn } = useAuthentication();

  if (!isLoggedIn.value) {
    savedChartsStore.$patch({
      isAttemptingToLoadChartLists: false,
      hasAttemptedToLoadChartLists: true,
      hasFailedToLoadChartLists: false
    });
    return;
  }

  let promiseObject;

  if (refreshChartListsPromise) {
    // there's already a request to refresh the user's ChartLists underway - await the current
    // request's promise rather than kicking off a new request
    promiseObject = refreshChartListsPromise;
  } else {
    promiseObject = _refreshChartLists();

    // store promise so that additional refreshChartLists function calls that are
    // made while the current network call is still underway can await this promise
    // rather than inefficiently making their own duplicate network requests for
    // the same data
    refreshChartListsPromise = promiseObject;

    promiseObject.then(() => {
      // remove the now-resolved promise from the cache
      refreshChartListsPromise = null;
    });
  }

  await promiseObject;
}
